<template>
    <div class="request-broker-tour">
        <form class="request-broker-tour__form"
              @submit.prevent="triggerGuard">
            <template v-if="isSuccess">
                <div class="request-broker-tour__success-icon">
                    <CheckStatus class-name="icon-stroke icon_color_blue" />
                </div>
                <p class="request-broker-tour__title">
                    Заявка принята
                </p>
                <p class="request-broker-tour__description">
                    Ваша заявка успешно отправлена,
                    <br>в&nbsp;ближайшее время с&nbsp;вами свяжется менеджер для&nbsp;уточнения&nbsp;деталей
                </p>
                <div class="request-broker-tour__form-action">
                    <STBaseButton text="Закрыть"
                                  modify="button_theme_white"
                                  @click="hide" />
                </div>
            </template>
            <template v-else-if="isError">
                <div class="request-broker-tour__alert-icon">
                    <IconAlert />
                </div>
                <p class="request-broker-tour__title">
                    Ой, что-то пошло не так
                </p>
                <p class="request-broker-tour__description">
                    Произошла непредвиденная ошибка<br>Пожалуйста попробуйте позже.
                </p>
                <div class="request-broker-tour__form-action">
                    <STBaseButton text="Хорошо"
                                  @click="hide" />
                </div>
            </template>
            <template v-else>
                <p class="request-broker-tour__title request-broker-tour__title_theme_top-level">
                    Записаться на брокер-тур
                </p>

                <div class="request-broker-tour__inputs">
                    <STInput
                        id="request-broker-tour-user-name"
                        v-model.trim="userName"
                        :modify="{'is-error': $v.userName.$dirty && $v.userName.$invalid}"
                        :error="getErrorText($v.userName)"
                        name="request-broker-tour-user-name"
                        label="Ваше имя"
                        @input="$v.userName.$touch()"
                        @focus="$v.userName.$touch()" />

                    <STInput
                        id="request-broker-tour-user-phone"
                        ref="userPhoneInput"
                        :value="userPhone"
                        :modify="{'is-error': $v.userPhone.$dirty && $v.userPhone.$invalid}"
                        :error="getErrorText($v.userPhone)"
                        name="request-broker-tour-user-phone"
                        label="Ваш номер телефона"
                        @input="onPhoneInput"
                        @focus="onPhoneFocus" />

                    <STInput
                        id="request-broker-tour-user-email"
                        v-model.trim="userEmail"
                        :modify="{'is-error': $v.userEmail.$dirty && $v.userEmail.$invalid}"
                        :error="getErrorText($v.userEmail)"
                        name="request-broker-tour-user-email"
                        label="Ваш e-mail"
                        @input="$v.userEmail.$touch()"
                        @focus="$v.userEmail.$touch()" />

                    <STSelect
                        id="request-broker-tour-project-select"
                        v-model="selectedProjectId"
                        :options="projects"
                        :multiple="false"
                        name="request-broker-tour-project-select"
                        label="Выберите проект" />

                    <STInput
                        id="request-broker-tour-guests-count"
                        v-model="guestsCount"
                        v-mask="'##'"
                        name="request-broker-tour-guests-count"
                        label="Количество человек" />
                </div>

                <div :class="['request-broker-tour__form-submit', {'is-loading': isLoading}]">
                    <STBaseButton text="Отправить заявку"
                                  icon-load="IconLoad"
                                  modify="button_full_width"
                                  :is-loading="isLoading"
                                  :disabled="!isFieldsFilled"
                                  @click="requestBrokerTour" />
                </div>
            </template>
        </form>
    </div>
</template>

<script>
import IconAlert from '@/components/Icons/Alert';
import CheckStatus from '@/components/Icons/CheckStatus';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';

import {mapActions, mapGetters} from 'vuex';
import {formatPhone, handlePhoneFocus, handlePhoneInput, isPhoneComplete} from '@/helpers/phone';

const onlyLetters = value => {
    if (!value) {
        return false;
    }

    if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(value)) {
        return false;
    }

    if (value.startsWith('-') || value.endsWith('-')) {
        return false;
    }

    return (value.match(/-/g) || []).length <= 3;
};

export default {
    name: 'RequestBrokerTour',

    components: {
        IconAlert,
        CheckStatus
    },

    validations: {
        userName: {
            required,
            onlyLetters,
            minLength: minLength(3),
            maxLength: maxLength(40)
        },
        userPhone: {
            required,
            phoneLength: isPhoneComplete
        },
        userEmail: {
            required,
            emailTest(value) {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            }
        }
    },

    data() {
        return {
            isSuccess        : false,
            isError          : false,
            isLoading        : false,
            userName         : '',
            userPhone        : '',
            userEmail        : '',
            selectedProjectId: '',
            guestsCount      : null,
        };
    },

    computed: {
        ...mapGetters({
            complexes     : 'GET_DISTRICT_LIST',
            userDataLoaded: 'GET_IS_USER_DATA_LOADED',
            userData      : 'GET_USER_DATA',
        }),

        isFieldsFilled() {
            return isPhoneComplete(this.userPhone) && !this.$v.$invalid && this.selectedProjectId && this.guestsCount && this.guestsCount > 0;
        },

        projects() {
            if (!this.complexes?.length) {
                return [];
            }

            return this.complexes.map((complex) => ({
                text : complex.name,
                value: complex.id
            }));
        }
    },

    mounted() {
        if (this.userDataLoaded) {
            this.applyUserData(this.userData);
        }
    },

    methods: {
        ...mapActions(['sendBrokerTourRequest']),

        applyUserData(data) {
            this.userName = `${data.firstName || ''} ${data.surname || ''}`.trim();
            this.userPhone = formatPhone(data.phone);
            this.userEmail = data.email;
        },

        getErrorText(value) {
            switch (false) {
                case value.required:
                    return 'Это обязательное поле';
                case value.minLength:
                    return `Минимальная длина ${value.$params?.minLength?.min} символа`;
                case value.maxLength:
                    return `Максимальная длина длина ${value.$params?.maxLength?.max} символов`;
                case value.letter:
                    return 'Допускаются только символы алфавита';
                case value.emailTest:
                    return 'Адрес электронной почты введён неправильно';
                case value.phoneLength:
                    return 'Это обязательное поле';
            }
        },

        /**
         * Селект при открытии триггерит сабмит формы
         * Данный метод блокирует случайные встроенные сабмиты не по кнопке
         * @return {null}
         */
        triggerGuard() {
            return null;
        },

        /**
         * Отправляет запрос на обратную связь
         */
        async requestBrokerTour() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.isError = false;
            this.isSuccess = false;
            this.isLoading = true;

            const payload = {
                name       : this.userName,
                phone      : this.userPhone,
                email      : this.userEmail,
                projectId  : this.selectedProjectId,
                peopleCount: this.guestsCount
            };

            try {
                const { data } = await this.sendBrokerTourRequest(payload);

                if (+data.status === 0 || data.error) {
                    this.handleRequestError(data.error);

                    return;
                }

                this.isLoading = false;
                this.isError = false;
                this.isSuccess = true;
            } catch(error) {
                this.handleRequestError(error);
            }
        },

        /**
         * Закрыть попап
         */
        hide() {
            this.$v.$reset();
            this.$emit('close');
        },

        /**
         * Обработка ошибки отправки формы
         */
        handleRequestError(data) {
            this.isLoading = false;
            this.isSuccess = false;
            this.isError = true;

            console.error('Ошибка отправки формы:', JSON.stringify(data));
        },

        onPhoneFocus() {
            handlePhoneFocus(this, 'userPhone', 'userPhoneInput');
            this.$v.userPhone.$touch();
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'userPhone', 'userPhoneInput', value);
            this.$v.userPhone.$touch();
        },
    }
};
</script>

<style lang="scss">
@import "RequestBrokerTour";
</style>
